import React from 'react';
import { Helmet } from 'react-helmet';
import LegalNav from '../components/LegalNav';

const DMCA = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Helmet>
        <title>Copyright & DMCA Policy | LTV VOD</title>
        <meta name="description" content="LTV VOD's Copyright and DMCA Policy. Learn how we handle copyright infringement claims." />
        <link rel="canonical" href="https://ltvproduction.com/dmca" />
      </Helmet>
      
      <LegalNav />

      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="text-4xl font-bold mb-2">Copyright & DMCA Policy</h1>
        <p className="text-gray-500 text-sm mb-8">Last updated: March 2026</p>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">1. Our Commitment to Copyright</h2>
          <p className="text-gray-300 mb-4">LTV Production respects the intellectual property rights of others and expects our users to do the same. All content on our platform is either:</p>
          <ul className="list-disc pl-6 text-gray-300 space-y-2">
            <li>Original content produced by LTV Production</li>
            <li>Licensed content from authorized rights holders</li>
            <li>Content uploaded by verified creators with proper rights</li>
          </ul>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">2. DMCA Notice and Takedown Procedure</h2>
          <p className="text-gray-300 mb-4">If you believe that your copyrighted work has been copied and is accessible on our platform in a way that constitutes copyright infringement, you may submit a notification pursuant to the Digital Millennium Copyright Act ("DMCA").</p>
          
          <h3 className="text-lg text-purple-400 mt-6 mb-3">Required Information for DMCA Notices</h3>
          <ol className="list-decimal pl-6 text-gray-300 space-y-2">
            <li><strong>Identification of the copyrighted work</strong> you claim has been infringed.</li>
            <li><strong>Identification of the material</strong> that you claim is infringing, with enough detail for us to locate it.</li>
            <li><strong>Your contact information:</strong> Name, address, telephone number, and email address.</li>
            <li><strong>A statement</strong> that you have a good faith belief that use of the material is not authorized.</li>
            <li><strong>A statement</strong> that the information is accurate, and under penalty of perjury, that you are authorized to act.</li>
            <li><strong>Your physical or electronic signature.</strong></li>
          </ol>
          
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-5 mt-6">
            <h3 className="text-purple-400 font-semibold mb-3">Send DMCA Notices To:</h3>
            <p className="text-gray-300"><strong>Email:</strong> dmca@ltvproduction.com</p>
            <p className="text-gray-300"><strong>Subject Line:</strong> DMCA Takedown Request</p>
          </div>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">3. Counter-Notification</h2>
          <p className="text-gray-300">If you believe your content was wrongly removed due to a DMCA notice, you may submit a counter-notification including your signature, identification of removed material, a statement under penalty of perjury, and consent to court jurisdiction.</p>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">4. Repeat Infringers</h2>
          <p className="text-gray-300">In accordance with the DMCA, we will terminate the accounts of users who are repeat infringers.</p>
        </section>
        
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b border-gray-800">5. Contact Information</h2>
          <p className="text-gray-300">For copyright-related inquiries: <a href="mailto:legal@ltvproduction.com" className="text-purple-400">legal@ltvproduction.com</a></p>
          <p className="text-gray-300">General Support: <a href="mailto:support@ltvproduction.com" className="text-purple-400">support@ltvproduction.com</a></p>
        </section>
      </main>
      
      {/* Footer */}
      <footer className="bg-[#111] border-t border-gray-800 py-10 mt-10">
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-semibold mb-4">Platform</h3>
              <a href="/vod" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">LTV VOD</a>
              <a href="/vod/browse" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Browse Videos</a>
              <a href="/live" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Live Events</a>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <a href="/about" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">About Us</a>
              <a href="/contact" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Contact</a>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <a href="/privacy" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Privacy Policy</a>
              <a href="/terms" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Terms of Service</a>
              <a href="/dmca" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Copyright/DMCA</a>
              <a href="/content-policy" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">Content Policy</a>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <a href="mailto:support@ltvproduction.com" className="block text-gray-400 text-sm mb-2 hover:text-purple-400">support@ltvproduction.com</a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-5 text-gray-500 text-sm">
            © 2026 LTV Production. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DMCA;
